import {useEffect, useRef, useState} from 'react';
import {
    Box, 
    Button, 
    Heading, 
    Select, 
    Input, 
    HStack, 
    Flex, 
    CloseButton,
    useDisclosure,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter
} from '@chakra-ui/react';
import {PopupButton, PopupLayout} from "../../../PopupLayout/PopupLayout";

const StoichWizard = (props) => {
    const [state, setState] = useState({
        step: 1,
        conversion: 50,
        basis: '',
        components: [],
        incomingComponents: [],
        error: null,
        rx_Data: props.element.attributes['equipment_Properties']['rx_Data'] || {}
    });
    const componentRef = useRef(null);
    const unitName = props.element.attributes.attrs.shapename.innerHTML.replace('<p>', '').replace('</p>', '');
    
    // For the alert dialog
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();

    useEffect(() => {
        // Simplified component handling - only using components
        if (!props.element.attributes.components) {
            setState({...state, components: []})
        } else {
            let newComponents = {}
            Object.keys(props.element.attributes.components).forEach(key => {
                newComponents[key] = {...props.element.attributes.components[key]}
            })
            setState(prev => ({...prev, components: newComponents}))
        }

        

        // Set incoming components
        let allComponents = props.element.attributes.components || {};
        let rxComponents = props.element.attributes.RX_components || {};

        // Filter out components that appear in RX_components
        let incomingComponents = {};
        console.log(allComponents);
        console.log(rxComponents);;
        Object.keys(allComponents).forEach(key => {
            if (!rxComponents[key]) {
                incomingComponents[key] = allComponents[key];
            }
        });
        console.log(incomingComponents);

        // Check if there are any components left after filtering
        if (Object.keys(incomingComponents).length === 0) {
            // Handle the case where no components are left
            console.error("No valid components available for selection - all components are in RX_components");
            setState(prev => ({
                ...prev, 
                incomingComponents: {},
                error: "No valid components available for the dropdown. All components are by-products."
            }));
            onOpen(); // Open the alert dialog when error is detected
        } else {
            setState(prev => ({...prev, incomingComponents: incomingComponents, error: null}));
        }

        const defaultBasis = props.element.attributes['equipment_Properties']['basis'] || Object.keys(incomingComponents)[0] || '';

        console.log("Default basis:", defaultBasis);

        // Set conversion and basis values
        setState(prev => ({
            ...prev,
            conversion: props.element.attributes['equipment_Properties']['conversion'] || '0',
            basis: defaultBasis
        }))
        
    }, [])

    const updateStoich = (name, value) => {
        // Only allow numbers, decimal points, and minus signs
        let numericValue = value.replace(/[^\d.-]/g, '');
        
        // Handle multiple minus signs - only allow at start
        if (numericValue.split('-').length > 2) {
            numericValue = '-' + numericValue.replace(/-/g, '');
        }
        
        // Allow minus sign only at the start
        if (numericValue.indexOf('-') > 0) {
            numericValue = numericValue.replace(/-/g, '');
        }
        
        setState(prevState => ({
            ...prevState,
            rx_Data: {
                ...prevState.rx_Data, 
                [name]: {
                    stoich: numericValue,
                    molmass: state.components[name].M
                }
            }
        }));
    };

    // Rest of the component remains the same
    const addSpecLabels = () => Object.keys(state.components).map(name => (
        <HStack key={name} marginBlockEnd={4}>
            <Box size="sm" width="100px">
                {name}
            </Box>
            <Box size="sm" width="40%">{state.components[name].M} (g/Mol)</Box>
            <Input width="30%"
                   value={state.rx_Data[name]?.stoich ?? '0'}
                   onChange={e => updateStoich(name, e.target.value)}
            />
        </HStack>
    ));

    const addConversionSection = () => {
        const options = Object.keys(state.incomingComponents);
        return (
            <HStack key="conversion-section" marginBlockEnd={4}>
                <Select value={state.basis} onChange={e => {
                    console.log("Selected basis from dropdown:", e.target.value);
                    setState(prevState => ({...prevState, basis: e.target.value}))
                }}>
                    {options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Select>
                <Input
                    value={state.conversion}
                    onChange={e => setState(prevState => ({...prevState, conversion: e.target.value}))}
                    placeholder="%"
                />
            </HStack>
        );
    };

    const finish = () => {
        props.element.attributes['equipment_Properties']['conversion'] = state.conversion;
        props.element.attributes['equipment_Properties']['basis'] = state.basis;
        props.element.attributes['equipment_Properties']['rx_Data'] = state.rx_Data;
        props.resolve();
    };

    return (
        <div id="StoichWizard">
            <PopupLayout
                minWidth="500px"
                componentRef={componentRef}
                pageX={window.innerWidth / 2 - 250}
                pageY={window.innerHeight / 2 - 250}
                closeFunction={props.resolve}
                title={unitName ? `Provide Stoichiometry for ${unitName.replace(/<br>/g, '')}` : ''}
                buttons={<PopupButton onClick={() => finish()}>Finish</PopupButton>}
            >
                <Flex ml={2} mr={2} mt={3} flexDirection="column" overflowY="auto" maxHeight="400px">
                    {!state.error ? (
                        <>
                            {addSpecLabels()}
                            <Heading size="sm" mb={4}>Specify Conversion (%)</Heading>
                            {addConversionSection()}
                        </>
                    ) : null}
                </Flex>
            </PopupLayout>

            {/* Alert Dialog for error messages */}
            <AlertDialog
                isOpen={isOpen && state.error !== null}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Error
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {state.error}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => {
                                onClose();
                                props.resolve(); // Close the popup when the alert is dismissed
                            }}>
                                Close
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </div>
    );
};

export default StoichWizard;